import { useState } from "react";
import type { Difficulty } from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { useI18n } from "../../i18n/index.js";
import { EndScreen, type RoundSummary } from "../../components/game/EndScreen.js";
import { CrowflightStart, type CrowflightOptions } from "./CrowflightStart.js";
import { CrowflightPlay } from "./CrowflightPlay.js";
import { fetchCrowflightNewGame, type CrowflightNewGame } from "./api.js";
import styles from "./Crowflight.module.css";

type Phase =
  | { kind: "start" }
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "playing"; game: CrowflightNewGame; difficulty: Difficulty }
  | { kind: "finished"; rounds: RoundSummary[]; totalScore: number; maxScore: number; difficulty: Difficulty };

export function Crowflight() {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>({ kind: "start" });
  const [lastOptions, setLastOptions] = useState<CrowflightOptions | null>(null);

  async function startGame(options: CrowflightOptions) {
    setLastOptions(options);
    setPhase({ kind: "loading" });
    try {
      const game = await fetchCrowflightNewGame(options.region, options.difficulty);
      setPhase({ kind: "playing", game, difficulty: options.difficulty });
    } catch {
      setPhase({ kind: "error" });
    }
  }

  function handleFinish(roundSummaries: RoundSummary[], totalScore: number, difficulty: Difficulty) {
    const maxScore = roundSummaries.reduce((sum, r) => sum + r.maxPoints, 0);
    setPhase({ kind: "finished", rounds: roundSummaries, totalScore, maxScore, difficulty });
  }

  function handleReplay() {
    if (lastOptions) void startGame(lastOptions);
  }

  if (phase.kind === "start") {
    return <CrowflightStart onStart={startGame} />;
  }

  if (phase.kind === "loading") {
    return (
      <main className={styles.statusMain}>
        <p>{t("common.loading")}</p>
      </main>
    );
  }

  if (phase.kind === "error") {
    return (
      <main className={styles.statusMain}>
        <p className={styles.error}>{t("crowflight.play.gameLoadError")}</p>
        <button type="button" className={styles.retryButton} onClick={() => setPhase({ kind: "start" })}>
          {t("timeslip.start.startButton")}
        </button>
      </main>
    );
  }

  if (phase.kind === "playing") {
    return (
      <CrowflightPlay
        game={phase.game}
        difficulty={phase.difficulty}
        onFinish={(rounds, totalScore) => handleFinish(rounds, totalScore, phase.difficulty)}
      />
    );
  }

  return (
    <EndScreen
      gameName={GAME_BRANDING.crowflight.name}
      difficulty={phase.difficulty}
      totalScore={phase.totalScore}
      maxScore={phase.maxScore}
      rounds={phase.rounds}
      onReplay={handleReplay}
    />
  );
}
